const FILE_META_FIELDS = [
  'storage_path',
  'file_type',
  'pages',
  'source_pages',
  'answer_text',
  'answer_text_source',
  'answer_text_truncated',
  'answer_text_warning',
  'attachments',
  'split_student_id',
  'source_file_name',
  'split_confidence',
  'split_warnings',
  'split_strategy',
  'submission_id',
  'scan_group_id',
  'client_student_id',
];

const STABLE_ID_FIELDS = [
  'split_student_id',
  'submission_id',
  'scan_group_id',
  'client_student_id',
];

const hasValue = (value) => (
  value !== undefined
  && value !== null
  && value !== ''
  && (!Array.isArray(value) || value.length > 0)
);

const pickFirstValue = (...values) => values.find(hasValue);

const isGenericStudentName = (value) => {
  const name = String(value || '').trim();
  return name === '학생' || name === '재채점' || /^학생\s*\d+$/.test(name);
};

export const normalizeStudentNameForMatch = (value) => String(value || '').trim();

export const isSyntheticScanAnswerText = (value) => (
  /^\[스캔 PDF 페이지 [^\]]+\]\s*/.test(String(value || '').trim())
);

export const normalizeScanAnswerTextCache = (items = []) => (
  (Array.isArray(items) ? items : []).map((item) => {
    if (!isSyntheticScanAnswerText(item?.answer_text)) return item;
    return {
      ...item,
      answer_text: '',
      answer_text_source: 'scan_ocr_failed',
      answer_text_warning: '스캔 답안 원문을 인식하지 못했어요.',
    };
  })
);

const normalizeFileNameForMatch = (item) => String(
  item?.fileName || item?.filename || item?.source_file_name || ''
).trim();

const normalizePagesForMatch = (item) => {
  const value = item?.source_pages?.length > 0 ? item.source_pages : item?.pages;
  return Array.isArray(value)
    ? value.map(page => String(page ?? '').trim()).filter(Boolean).join(',')
    : String(value || '').trim();
};

const hasStableIdConflict = (item, result) => STABLE_ID_FIELDS.some((field) => (
  hasValue(item?.[field])
  && hasValue(result?.[field])
  && String(item[field]) !== String(result[field])
));

const hasMatchingStableId = (item, result) => STABLE_ID_FIELDS.some((field) => (
  hasValue(item?.[field])
  && hasValue(result?.[field])
  && String(item[field]) === String(result[field])
));

const namesAreCompatible = (item, result) => {
  const itemName = normalizeStudentNameForMatch(item?.student_name || item?.studentName || item?.name);
  const resultName = normalizeStudentNameForMatch(result?.student_name || result?.studentName || result?.name);
  if (!itemName || !resultName) return true;
  if (isGenericStudentName(itemName) || isGenericStudentName(resultName)) return true;
  return itemName === resultName;
};

const findMatchingItemIndex = (items, result, resultIndex, usedIndexes = new Set()) => {
  const list = Array.isArray(items) ? items : [];
  const availableIndexes = list
    .map((_, index) => index)
    .filter(index => !usedIndexes.has(index));
  const resultName = normalizeStudentNameForMatch(
    result?.student_name || result?.studentName || result?.name
  );
  const resultFileName = normalizeFileNameForMatch(result);
  const resultPages = normalizePagesForMatch(result);

  const stableIdIndex = availableIndexes.find(index => (
    hasMatchingStableId(list[index], result)
  ));
  if (stableIdIndex !== undefined) return stableIdIndex;

  if (resultName && !isGenericStudentName(resultName)) {
    const nameIndex = availableIndexes.find((index) => {
      const itemName = normalizeStudentNameForMatch(
        list[index]?.student_name || list[index]?.studentName || list[index]?.name
      );
      return itemName === resultName && !hasStableIdConflict(list[index], result);
    });
    if (nameIndex !== undefined) return nameIndex;
  }

  if (resultFileName) {
    const fileIndex = availableIndexes.find((index) => (
      normalizeFileNameForMatch(list[index]) === resultFileName
      && (!resultPages || !normalizePagesForMatch(list[index]) || normalizePagesForMatch(list[index]) === resultPages)
      && !hasStableIdConflict(list[index], result)
      && namesAreCompatible(list[index], result)
    ));
    if (fileIndex !== undefined) return fileIndex;
  }

  const explicitIndex = availableIndexes.find((index) => (
    Number(list[index]?.result_index) === resultIndex
    && !hasStableIdConflict(list[index], result)
    && namesAreCompatible(list[index], result)
  ));
  if (explicitIndex !== undefined) return explicitIndex;

  if (
    Number.isInteger(resultIndex)
    && availableIndexes.includes(resultIndex)
    && !hasStableIdConflict(list[resultIndex], result)
    && namesAreCompatible(list[resultIndex], result)
  ) {
    return resultIndex;
  }

  return -1;
};

export const getScoringStudentName = (result, fallback = '') => {
  const name = pickFirstValue(result?.studentName, result?.student_name);
  return normalizeStudentNameForMatch(name || fallback);
};

const inferAnswerTextSource = (next, previous, result) => {
  if (hasValue(next?.answer_text)) {
    return pickFirstValue(next.answer_text_source, 'cache_next');
  }
  if (hasValue(previous?.answer_text)) {
    return pickFirstValue(previous.answer_text_source, 'cache_previous');
  }
  if (hasValue(result?.answer_text)) {
    return pickFirstValue(result.answer_text_source, 'result');
  }
  return pickFirstValue(next?.answer_text_source, previous?.answer_text_source, result?.answer_text_source, '');
};

const resolveStudentName = (previousResult = {}, nextResult = {}, index) => {
  const previousName = getScoringStudentName(previousResult);
  const nextName = getScoringStudentName(nextResult);

  if (nextName && (!isGenericStudentName(nextName) || !previousName)) {
    return nextName;
  }
  if (previousName) {
    return previousName;
  }
  if (nextName) {
    return nextName;
  }
  return Number.isInteger(index) ? `학생 ${index + 1}` : '';
};

const resolveFileName = (previousResult = {}, nextResult = {}, studentName = '') => {
  const previousName = pickFirstValue(previousResult.fileName, previousResult.filename);
  const nextName = pickFirstValue(nextResult.fileName, nextResult.filename);

  if (hasValue(previousName)) {
    return previousName;
  }
  if (hasValue(nextName) && normalizeStudentNameForMatch(nextName) !== studentName) {
    return nextName;
  }
  return nextName || '';
};

export const mergeScoringResultMeta = (previousResult = {}, nextResult = {}, index) => {
  const previous = previousResult && typeof previousResult === 'object' ? previousResult : {};
  const next = nextResult && typeof nextResult === 'object' ? nextResult : {};
  const merged = { ...previous, ...next };
  const studentName = resolveStudentName(previous, next, index);
  const fileName = resolveFileName(previous, next, studentName);

  if (studentName) {
    merged.studentName = studentName;
    merged.student_name = studentName;
  }

  if (fileName) {
    merged.fileName = fileName;
    merged.filename = fileName;
  }

  FILE_META_FIELDS.forEach((field) => {
    const previousValue = previous[field];
    const nextValue = next[field];
    if (hasValue(previousValue)) {
      merged[field] = previousValue;
    } else if (hasValue(nextValue)) {
      merged[field] = nextValue;
    }
  });

  if (!Array.isArray(next.revision_history) && Array.isArray(previous.revision_history)) {
    merged.revision_history = previous.revision_history;
  }

  return merged;
};

export const mergeScoringResultsMeta = (previousResults = [], nextResults = []) => {
  const previousList = Array.isArray(previousResults) ? previousResults : [];
  const nextList = Array.isArray(nextResults) ? nextResults : [];
  if (nextList.length === 0) return previousList;
  const usedPreviousIndexes = new Set();

  return nextList.map((nextResult, index) => {
    const previousIndex = findMatchingItemIndex(
      previousList,
      nextResult,
      index,
      usedPreviousIndexes,
    );
    if (previousIndex >= 0) usedPreviousIndexes.add(previousIndex);
    return mergeScoringResultMeta(
      previousIndex >= 0 ? previousList[previousIndex] : {},
      nextResult,
      index,
    );
  });
};

export const mergeCachedAnswerTextsWithResults = (
  previousTexts = [],
  nextTexts = [],
  mergedResults = [],
) => {
  const previousList = Array.isArray(previousTexts) ? previousTexts : [];
  const nextList = Array.isArray(nextTexts) ? nextTexts : [];
  const resultList = Array.isArray(mergedResults) ? mergedResults : [];
  const resultCount = resultList.length || Math.max(previousList.length, nextList.length);
  const usedPreviousIndexes = new Set();
  const usedNextIndexes = new Set();

  return Array.from({ length: resultCount }, (_, index) => {
    const result = resultList[index] || {};
    const previousIndex = resultList.length > 0
      ? findMatchingItemIndex(previousList, result, index, usedPreviousIndexes)
      : (index < previousList.length ? index : -1);
    const nextIndex = resultList.length > 0
      ? findMatchingItemIndex(nextList, result, index, usedNextIndexes)
      : (index < nextList.length ? index : -1);
    if (previousIndex >= 0) usedPreviousIndexes.add(previousIndex);
    if (nextIndex >= 0) usedNextIndexes.add(nextIndex);

    const previous = previousIndex >= 0 ? previousList[previousIndex] : {};
    const next = nextIndex >= 0 ? nextList[nextIndex] : {};
    const resultName = getScoringStudentName(result);
    const previousName = getScoringStudentName(previous);
    const nextName = getScoringStudentName(next);
    const studentName = (
      resultName && !isGenericStudentName(resultName)
        ? resultName
        : (nextName || previousName || resultName || `학생 ${index + 1}`)
    );
    const answerText = pickFirstValue(next.answer_text, previous.answer_text, result.answer_text, '');
    const answerTextSource = inferAnswerTextSource(next, previous, result);

    return {
      ...previous,
      ...next,
      result_index: pickFirstValue(next.result_index, previous.result_index, result.result_index, index),
      student_name: studentName,
      answer_text: answerText,
      answer_text_source: answerTextSource,
      answer_text_warning: pickFirstValue(next.answer_text_warning, previous.answer_text_warning, result.answer_text_warning, ''),
      fileName: pickFirstValue(previous.fileName, next.fileName, result.fileName, result.filename, ''),
      filename: pickFirstValue(previous.filename, next.filename, result.filename, result.fileName, ''),
      storage_path: pickFirstValue(previous.storage_path, next.storage_path, result.storage_path, ''),
      file_type: pickFirstValue(previous.file_type, next.file_type, result.file_type, ''),
      pages: pickFirstValue(previous.pages, next.pages, result.pages, ''),
      source_pages: pickFirstValue(previous.source_pages, next.source_pages, result.source_pages, []),
      answer_text_truncated: Boolean(pickFirstValue(next.answer_text_truncated, previous.answer_text_truncated, result.answer_text_truncated, false)),
      attachments: pickFirstValue(previous.attachments, next.attachments, result.attachments, []),
      split_student_id: pickFirstValue(previous.split_student_id, next.split_student_id, result.split_student_id, null),
      source_file_name: pickFirstValue(previous.source_file_name, next.source_file_name, result.source_file_name, ''),
      split_confidence: pickFirstValue(previous.split_confidence, next.split_confidence, result.split_confidence, ''),
      split_warnings: pickFirstValue(previous.split_warnings, next.split_warnings, result.split_warnings, []),
      split_strategy: pickFirstValue(previous.split_strategy, next.split_strategy, result.split_strategy, ''),
      submission_id: pickFirstValue(previous.submission_id, next.submission_id, result.submission_id, null),
      scan_group_id: pickFirstValue(previous.scan_group_id, next.scan_group_id, result.scan_group_id, null),
      client_student_id: pickFirstValue(previous.client_student_id, next.client_student_id, result.client_student_id, null),
    };
  });
};

export const hasCachedAnswerText = (item) => hasValue(item?.answer_text);

export const findInvalidMethodCStudent = (students = [], minimumLength = 10) => (
  (Array.isArray(students) ? students : []).find(
    student => String(student?.answer_text || '').trim().length <= minimumLength
  ) || null
);

export const resolveAnswerTextForResult = ({
  cachedAnswerTexts = [],
  results = [],
  result = {},
  resultIndex = 0,
  batchStudents = [],
  singleAnswerText = '',
} = {}) => {
  const resultList = Array.isArray(results) ? results : [];
  const targetResult = result && typeof result === 'object' ? result : (resultList[resultIndex] || {});
  const mergedCached = mergeCachedAnswerTextsWithResults(cachedAnswerTexts, [], resultList);
  const indexedCache = mergedCached[resultIndex] || {};
  const targetName = normalizeStudentNameForMatch(
    getScoringStudentName(targetResult)
      || getScoringStudentName(indexedCache)
      || `학생 ${resultIndex + 1}`
  );
  const namedCache = hasCachedAnswerText(indexedCache)
    ? indexedCache
    : mergedCached.find((item) => (
      hasCachedAnswerText(item)
      && targetName
      && normalizeStudentNameForMatch(item.student_name) === targetName
    )) || {};
  const methodCStudents = Array.isArray(batchStudents)
    ? batchStudents.map(student => ({
      ...student,
      student_name: student?.student_name || student?.studentName || student?.name || '',
    }))
    : [];
  const methodCIndex = findMatchingItemIndex(
    methodCStudents,
    targetResult,
    resultIndex,
  );
  const methodCStudent = methodCIndex >= 0 ? methodCStudents[methodCIndex] : {};
  const answerText = pickFirstValue(
    indexedCache.answer_text,
    namedCache.answer_text,
    targetResult.answer_text,
    methodCStudent.answer_text,
    singleAnswerText,
  ) || '';
  const source = (() => {
    if (hasValue(indexedCache.answer_text)) return pickFirstValue(indexedCache.answer_text_source, 'cache_index');
    if (hasValue(namedCache.answer_text)) return pickFirstValue(namedCache.answer_text_source, 'cache_name');
    if (hasValue(targetResult.answer_text)) return pickFirstValue(targetResult.answer_text_source, 'result');
    if (hasValue(methodCStudent.answer_text)) return pickFirstValue(methodCStudent.answer_text_source, 'method_c_state');
    if (hasValue(singleAnswerText)) return 'single_text_state';
    return '';
  })();

  return {
    ...indexedCache,
    ...namedCache,
    student_name: targetName,
    answer_text: answerText,
    answer_text_source: source,
    answer_text_warning: pickFirstValue(
      indexedCache.answer_text_warning,
      namedCache.answer_text_warning,
      targetResult.answer_text_warning,
      methodCStudent.answer_text_warning,
      '',
    ),
    fileName: pickFirstValue(
      indexedCache.fileName,
      namedCache.fileName,
      targetResult.fileName,
      targetResult.filename,
      methodCStudent.fileName,
      methodCStudent.filename,
      methodCStudent.source_file_name,
      '',
    ),
    filename: pickFirstValue(
      indexedCache.filename,
      namedCache.filename,
      targetResult.filename,
      targetResult.fileName,
      methodCStudent.filename,
      methodCStudent.fileName,
      methodCStudent.source_file_name,
      '',
    ),
    pages: pickFirstValue(indexedCache.pages, namedCache.pages, targetResult.pages, methodCStudent.pages, ''),
    source_pages: pickFirstValue(
      indexedCache.source_pages,
      namedCache.source_pages,
      targetResult.source_pages,
      methodCStudent.source_pages,
      [],
    ),
    split_student_id: pickFirstValue(
      indexedCache.split_student_id,
      namedCache.split_student_id,
      targetResult.split_student_id,
      methodCStudent.split_student_id,
      null,
    ),
    client_student_id: pickFirstValue(
      indexedCache.client_student_id,
      namedCache.client_student_id,
      targetResult.client_student_id,
      methodCStudent.client_student_id,
      null,
    ),
    source_file_name: pickFirstValue(
      indexedCache.source_file_name,
      namedCache.source_file_name,
      targetResult.source_file_name,
      methodCStudent.source_file_name,
      '',
    ),
    answer_text_truncated: Boolean(pickFirstValue(
      indexedCache.answer_text_truncated,
      namedCache.answer_text_truncated,
      targetResult.answer_text_truncated,
      methodCStudent.answer_text_truncated,
      false,
    )),
    result_index: resultIndex,
  };
};
